import CardWrapper from '../wrapper/card';
import { ComposeEmailParams } from '../../params/redux/email';
import CustomInput from '../reusable/input';
import { emailThreadSchema } from '../../schema/email_schema';
import { formatDateWithTime } from '../../helper';
import { settingSelectedEmail } from '../../redux/slices/email';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, Divider, Skeleton, Typography } from '@mui/material';
import { brand, gray } from '../../theme/themePrimitives';
import { fetchMailMessagesThunk, fetchThreadMessagesThunk, fetchUserMailsThunk, replyToThreadThunk } from '../../redux/thunks/email';
import { Form, Formik } from 'formik';
import { KeyboardArrowLeft, RepeatRounded } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';

const SelectedEmailComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const { selectedEmail, loaders, mailMessages } = useSelector((state: RootState) => state.email);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    });

    useEffect(() => {
        const fetchEmailThread = async () => {
            try {
                setIsLoading(true);
                await dispatch(fetchMailMessagesThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
            } catch (e) {
                //
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmailThread();
    }, []);

    const backNavigationHandler = async () => {
        try {
            await dispatch(fetchUserMailsThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
            dispatch(settingSelectedEmail(null));
        } catch (e) {
            //
        }
    };

    const fetchThreadMessages = async () => {
        try {
            setIsLoading(true);
            await dispatch(fetchThreadMessagesThunk({ thread_id: (selectedEmail && selectedEmail?.id) || 0, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
            await dispatch(fetchMailMessagesThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
        } catch (e) {
            //
        } finally {
            setIsLoading(false);
        }
    };

    const replyToThreadHandler = async (
        values: { message: string; },
        { resetForm }: { resetForm: () => void }
    ) => {
        try {
            const data: ComposeEmailParams = {
                thread_id: selectedEmail?.thread_id,
                body: values.message,
                client_id: id ? parseInt(id as string, 10) : 0
            };
            await dispatch(replyToThreadThunk(data)).unwrap();
            resetForm();
        } catch (e) {
            //
        }
    };

    return <Box>
        <Button startIcon={<KeyboardArrowLeft />} variant="text" color="secondary" disabled={loaders.isCreating || loaders.isLoading} onClick={backNavigationHandler}>Back</Button>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" color="textDisabled">{selectedEmail?.to_email}</Typography>
            <Typography variant="h6" color="textSecondary">{selectedEmail?.subject}</Typography>
            <Box><Button variant="text" color="primary" startIcon={<RepeatRounded />} disabled={loaders.isLoading} onClick={fetchThreadMessages}>
                Refresh
            </Button></Box>
        </Box>
        <CardWrapper>
            <Box minHeight="40vh" maxHeight="40vh" px={1} overflow="auto" display="flex" flexDirection="column" gap={2} >
                {
                    isLoading
                        ? (
                            Array.from({ length: 5 }).map((_, index) => (
                                <Box key={index} display="flex" alignItems="center" justifyContent={index % 2 === 0 ? 'start' : 'end'} width="100%">
                                    <Box width="max-content">
                                        <Skeleton variant="rounded" width={200} height={60} />
                                        <Skeleton variant="text" width={100} />
                                    </Box>
                                </Box>
                            ))
                        )
                        : mailMessages.map((mes) => <Box display="flex" alignItems="center" width="100%" justifyContent={mes.is_sent === 1 ? 'end' : 'start'} key={mes.id}>
                            <Box width="50%"><Box
                                bgcolor={mes.is_sent === 1 ? brand[300] : gray[300]} px={2} py={1} sx={{
                                    borderTopLeftRadius: mes.is_sent === 1 ? '10px' : '0',
                                    borderTopRightRadius: mes.is_sent === 1 ? '0' : '10px',
                                    borderBottomRightRadius: '10px',
                                    borderBottomLeftRadius: '10px'
                                }}>
                                <Box
                                    color={mes.is_sent === 1 ? 'white' : 'black'}
                                    width="max-content"
                                    height="max-content"
                                    maxWidth="100%"
                                    overflow="wrap"
                                    dangerouslySetInnerHTML={{ __html: mes.body }}
                                />
                            </Box>
                                <Typography variant="body1" color="textDisabled">{formatDateWithTime(mes.created_at)}</Typography>
                            </Box>
                        </Box>)
                }
                <div ref={messagesEndRef} />
            </Box>
            <Divider />
            <Formik
                initialValues={{
                    message: ''
                }}
                validationSchema={emailThreadSchema}
                onSubmit={replyToThreadHandler}
            >
                {({ values, setFieldValue, touched, errors }) => (
                    <Form>
                        <Box display="flex" mt={1} gap={2}>
                            <Box width="100%" height="16vh" >
                                <CustomInput
                                    type="quill"
                                    value={values.message}
                                    name="message"
                                    placeholder="Type content here..."
                                    disabled={loaders.isCreating || loaders.isLoading}
                                    error={errors.message && touched.message}
                                    quillHeight="12vh"
                                    onChangeForPhone={(value: string) => setFieldValue('message', value)}
                                />
                                {/* <CustomToolbar />
                                <ReactQuill
                                    onChange={(content) => setFieldValue('message', content)}
                                    value={values.message}
                                    placeholder="Type content here..."
                                    modules={quillModule}
                                    formats={quillFormats}
                                    theme="snow"
                                    readOnly={loaders.isCreating}
                                    style={{
                                        height: '12vh',
                                        borderBottomLeftRadius: '10px',
                                        borderBottomRightRadius: '10px',
                                        border:  ? `1px solid ${red[300]}` : 'none'
                                    }}
                                /> */}
                            </Box>
                            <Button variant="contained" size="large" type="submit" disabled={loaders.isCreating || loaders.isLoading}>Send</Button>
                        </Box>
                    </Form>
                )}
            </Formik>
        </CardWrapper>
    </Box>;
};

export default SelectedEmailComponent;